import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { galleryKeys } from './gallery';
import { firstErrorMessage } from './term-versions';

export type InstagramAccount = {
  id: string;
  username: string;
  tokenExpiresAt: string;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  createdAt: string;
};

export type InstagramImportResult = {
  account: string;
  imported: number;
  alreadyImported: number;
  unavailable: number;
};

const instagram = apiClient.instagram as any;

export const instagramKeys = {
  accounts: ['instagram', 'accounts'] as const,
};

async function call<T>(request: Promise<{ data: T }>) {
  try {
    return (await request).data;
  } catch (e: any) {
    throw firstErrorMessage(e?.value);
  }
}

type InstagramAccounts = {
  configured: boolean;
  accounts: InstagramAccount[];
  /** Importação dos posts antigos rodando no servidor. */
  importingHistory: boolean;
};

/**
 * Contas conectadas + se o servidor tem o app da Meta configurado. Durante a importação dos posts
 * antigos, atualiza sozinho (status das contas e fotos que vão entrando nos álbuns).
 */
export function useInstagramAccounts(enabled = true) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: instagramKeys.accounts,
    enabled,
    queryFn: async () => {
      const data = await call<InstagramAccounts>(instagram.accounts.get());
      if (data.importingHistory) void queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      return data;
    },
    refetchInterval: (query) => (query.state.data?.importingHistory ? 5000 : false),
  });
}

/** Traz todos os posts antigos das contas pros álbuns do Instagram (roda em segundo plano no servidor). */
export function useImportInstagramHistory() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: () => call<{ message: string }>(instagram['import-history'].post()),
    onSuccess: () =>
      toast.success('Importando os posts antigos. As fotos vão aparecendo nos álbuns.'),
    onError: (e: any) => toast.error(e?.message ?? 'Erro ao iniciar a importação'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: instagramKeys.accounts }),
  });
}

/** Vai pro login do Instagram; a volta cai em `/gallery?instagram=…`. */
export async function connectInstagram() {
  const { url } = await call<{ url: string }>(instagram.connect.get());
  window.location.href = url;
}

export function useDisconnectInstagram() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => call(instagram.accounts({ id }).delete()),
    onSuccess: () => toast.success('Conta desconectada'),
    onError: (e: any) => toast.error(e?.message ?? 'Erro ao desconectar a conta'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: instagramKeys.accounts }),
  });
}

/** Importa as fotos de um post pro álbum. O erro de validação ("Post não encontrado…") vai pra tela. */
export function useImportInstagramPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { albumId: string; url: string }) =>
      call<InstagramImportResult>(instagram.import.post(vars)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: galleryKeys.all }),
  });
}
